import {
    differenceInDays,
    endOfMonth,
    endOfWeek,
    getHours,
    getMinutes,
    isAfter,
    isBefore,
    isEqual,
    isSameDay,
    isSameMonth,
    isSameYear,
    isWithinInterval,
    parse,
    set,
    startOfMonth,
    startOfWeek,
} from 'date-fns';
import {toZonedTime} from 'date-fns-tz';
import {chunk} from 'lodash';
import {getRange} from 'utils/array';
import {JST_TIMEZONE} from 'utils/date';

export const selector = {
    date: (id: string, date: number) => `${id}-date-selector-date-${date}`,
    // nextButton: (id: string) => `${id}-date-selector-nav-next`,
    // previousButton: (id: string) => `${id}-date-selector-nav-previous`,
};

export const getSafeDate = (date: Date): Date =>
    set(date, {
        hours: 9,
        milliseconds: 0,
        minutes: 0,
        seconds: 0,
    });

export const getElementById = (id: string): HTMLButtonElement | null =>
    document.querySelector(`#${CSS.escape(id)}`);

const getEnabledDate = (id: string, index: number): string | undefined => {
    const dateId = selector.date(id, index);
    const element = getElementById(dateId);

    return element && !element.disabled ? dateId : undefined;
};

export const getFirstEnabledDate = (id: string): string | undefined => {
    let index = -1;

    while (index++ < 42) {
        const date = getEnabledDate(id, index);
        if (date) return date;
    }

    return undefined;
};

export const getLastEnabledDate = (id: string): string | undefined => {
    let index = 42;

    while (index-- > 0) {
        const date = getEnabledDate(id, index);
        if (date) return date;
    }

    return undefined;
};

export const isValidDate = (
    date: Date,
    minDate?: Date,
    maxDate?: Date,
): boolean => {
    if (!minDate && !maxDate) return true;

    if (minDate && maxDate) {
        return isWithinInterval(date, {end: maxDate, start: minDate});
    }

    if (maxDate) {
        return isBefore(date, maxDate) || isEqual(date, maxDate);
    }

    // TypeScript can't infer that minDate must have a value at this point
    // @ts-expect-error TODO: Fix this
    return isAfter(date, minDate) || isEqual(date, minDate);
};

export const isSameDate = (date1: Date, date2: Date): boolean =>
    isSameYear(date1, date2) &&
    isSameMonth(date1, date2) &&
    isSameDay(date1, date2);

export const hourOptions = getRange(0, 23).map((value) => ({
    label: String(value).padStart(2, '0'),
    value: String(value),
}));

export type MinuteStep = '01' | '05' | '10' | '15' | '30';

export const defaultMinuteStep: MinuteStep = '15';

const minuteSteps: Record<MinuteStep, number[]> = {
    '01': getRange(0, 59),
    '05': [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55],
    '10': [0, 10, 20, 30, 40, 50],
    '15': [0, 15, 30, 45],
    '30': [0, 30],
};

export const getMinuteOptions = (minuteStep: MinuteStep = defaultMinuteStep) =>
    minuteSteps[minuteStep].map((value) => ({
        label: String(value).padStart(2, '0'),
        value: String(value),
    }));

export const checkValidTime = (
    date: Date,
    minuteStep: MinuteStep = defaultMinuteStep,
) =>
    hourOptions.find((option) => option.value === String(getHours(date))) &&
    getMinuteOptions(minuteStep).find(
        (option) => option.value === String(getMinutes(date)),
    );

export const getDateFromId = (id: string, index: number): Date | null => {
    const dateId = selector.date(id, index);
    const element = getElementById(dateId);

    if (!element) return null;

    const ariaLabel = element.getAttribute('aria-label');
    if (!ariaLabel) return null;

    return getSafeDate(parse(ariaLabel, 'yyyy-MM-dd', new Date()));
};

export const getIdFromDate = (id: string, date: Date): null | string => {
    let index = -1;

    while (index++ < 42) {
        const elementDate = getDateFromId(id, index);

        if (elementDate && isSameDate(elementDate, date))
            return selector.date(id, index);
    }

    return null;
};

export const getCalendarRowCount = (date: Date): number => {
    const firstDate = startOfWeek(startOfMonth(date));
    const lastDate = endOfWeek(endOfMonth(date));

    return chunk(Array(differenceInDays(lastDate, firstDate) + 1), 7).length;
};

export const isWithinRange = (
    date: Date | undefined,
    minDate: Date | undefined,
    maxDate: Date | undefined,
) => {
    if (!date) return true;
    if (minDate && isBefore(date, minDate)) return false;

    return !(maxDate && isAfter(date, maxDate));
};

export const getDefaultDate = (
    minDate: Date | undefined,
    maxDate: Date | undefined,
) => {
    const today = new Date();
    const validDate =
        !minDate || isAfter(today, minDate)
            ? !maxDate || isBefore(today, maxDate)
                ? today
                : maxDate
            : minDate;

    const validDateInJst = toZonedTime(validDate, JST_TIMEZONE);

    return getSafeDate(validDateInJst);
};
